const PROFILE_STORAGE_KEY = "redo.support-v2.profile-ticket-load";
const PROFILE_QUERY_PARAM = "profileSupportTicketLoad";
const PROFILE_PREFIX = "[support-v2-profile]";

type ProfileMetadata = Record<string, unknown>;

export function isSupportTicketLoadProfilingEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.has(PROFILE_QUERY_PARAM) ||
      window.localStorage.getItem(PROFILE_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
}

export function logSupportTicketProfileEvent(
  label: string,
  metadata: ProfileMetadata = {},
): void {
  if (!isSupportTicketLoadProfilingEnabled()) {
    return;
  }

  console.debug(PROFILE_PREFIX, label, metadata);
}

export async function profileSupportTicketRequest<T>(
  label: string,
  metadata: ProfileMetadata,
  request: () => Promise<T>,
): Promise<T> {
  if (!isSupportTicketLoadProfilingEnabled()) {
    return request();
  }

  const startedAt = performance.now();
  try {
    const result = await request();
    logSupportTicketProfileEvent(label, {
      ...metadata,
      durationMs: Math.round(performance.now() - startedAt),
      ...getResultMetadata(result),
    });
    return result;
  } catch (error) {
    logSupportTicketProfileEvent(`${label}:error`, {
      ...metadata,
      durationMs: Math.round(performance.now() - startedAt),
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

function getResultMetadata(result: unknown): ProfileMetadata {
  if (!result || typeof result !== "object") {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const record = result as Record<string, unknown>;
  if (Array.isArray(record.items)) {
    return { itemCount: record.items.length };
  }
  if (Array.isArray(record.drafts)) {
    return { draftCount: record.drafts.length };
  }
  if (Array.isArray(record.orders)) {
    return { orderCount: record.orders.length };
  }
  if (Array.isArray(result)) {
    return { itemCount: result.length };
  }
  return {};
}
